import { ReactNode } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import {
  WELLNESS_DOMAINS_DISPLAY_LABELS,
  PEER_SUPPORT_EVENT_OUTCOME,
  PEER_SUPPORT_EVENT_OUTCOME_DISPLAY_LABELS,
} from '@/constants';
import { THealthDomain } from '@/types/Health.types';
import { extra, primary, secondary, text } from '@/theme/colors/palettes';
import getDomainIconFromDomain from '@/utils/getDomainIconFromDomain';
import { WeeklyReportDraft, total } from './model/types';
import WeeklyReportAssignmentBadge from './WeeklyReportAssignmentBadge';

const PreviewChip = ({ label, count }: { label: string; count: number }) => (
  <Flex
    align="center"
    gap={3}
    bg={extra.white}
    color={primary[500]}
    borderRadius="full"
    px={4}
    minH="42px"
    boxShadow={`0 2px 3px ${extra.mediumBlue5pct}`}
  >
    <Text fontWeight={800} fontSize="14px">
      {label}
    </Text>
    <Text
      as="span"
      fontWeight={900}
      fontSize="12px"
      lineHeight="20px"
      bg={primary[500]}
      color={extra.white}
      borderRadius="full"
      minW="20px"
      h="20px"
      px={1}
      textAlign="center"
    >
      {count}
    </Text>
  </Flex>
);

const PreviewSection = ({ title, count, children }: { title: string; count?: number; children: ReactNode }) => (
  <Box as="section" mt={10}>
    <Flex align="center" gap={4} wrap="wrap" mb={6}>
      <Text variant="urbanistBold16" fontSize="18px" lineHeight="22px" fontWeight={800} color={text.darkBlue}>
        {title}
      </Text>
      {count !== undefined && (
        <WeeklyReportAssignmentBadge color={text.mediumBlue}>{count} Selected</WeeklyReportAssignmentBadge>
      )}
    </Flex>
    {children}
  </Box>
);

const WeeklyReportPreview = ({
  draft,
  existingContactCount,
  domains,
  topicLabel,
}: {
  draft: WeeklyReportDraft;
  existingContactCount: number;
  domains: THealthDomain[];
  topicLabel: (topic: string) => string;
}) => (
  <>
    <Text variant="loraExtraSmallTitle" textAlign={'start'}>
      Let’s review your entries
    </Text>
    <Text color={text.mediumGray} mt={1} fontSize="14px" fontWeight={600}>
      Review your information before submitting.
    </Text>

    <PreviewSection title="Number of Contacts">
      <Flex
        align="center"
        justify="center"
        bg={extra.white}
        border={`1px solid ${extra.mediumGray30pct}`}
        borderRadius="8px"
        w="64px"
        h="53px"
        boxShadow="sm"
      >
        <Text fontWeight={800} fontSize="18px" lineHeight="16px">
          {draft.contactCount}
        </Text>
      </Flex>
    </PreviewSection>

    <PreviewSection title="Wholeness Domains" count={total(draft.domains)}>
      <Flex wrap="wrap" gap={2}>
        {domains
          .filter((domain) => draft.domains[domain])
          .map((domain) => (
            <PreviewChip key={domain} label={WELLNESS_DOMAINS_DISPLAY_LABELS[domain]} count={draft.domains[domain]} />
          ))}
      </Flex>
    </PreviewSection>

    <PreviewSection title="Topics Discussed">
      {domains
        .filter((domain) => draft.domains[domain])
        .map((domain) => {
          const DomainIcon = getDomainIconFromDomain(domain);
          return (
            <Box key={domain} mt={6}>
              <Flex align="center" gap={4} mb={4} wrap="wrap">
                <Flex align="center" gap={3}>
                  <Box aria-hidden="true" boxSize="26px" flexShrink={0}>
                    <DomainIcon color={text.mediumBlue} size={26} />
                  </Box>
                  <Text variant="urbanistBold16" as="h3">
                    {WELLNESS_DOMAINS_DISPLAY_LABELS[domain]}
                  </Text>
                </Flex>
                <WeeklyReportAssignmentBadge color={text.mediumBlue}>
                  {total(draft.topics[domain] ?? {})} Selected
                </WeeklyReportAssignmentBadge>
              </Flex>
              <Flex wrap="wrap" gap={2}>
                {Object.entries(draft.topics[domain] ?? {}).map(([topic, count]) => (
                  <PreviewChip key={topic} label={topicLabel(topic)} count={count} />
                ))}
              </Flex>
            </Box>
          );
        })}
    </PreviewSection>

    <PreviewSection title="Conversation Outcome" count={total(draft.outcomes)}>
      <Flex wrap="wrap" gap={2}>
        {PEER_SUPPORT_EVENT_OUTCOME.filter((outcome) => draft.outcomes[outcome]).map((outcome) => (
          <PreviewChip
            key={outcome}
            label={PEER_SUPPORT_EVENT_OUTCOME_DISPLAY_LABELS[outcome]}
            count={draft.outcomes[outcome]}
          />
        ))}
      </Flex>
    </PreviewSection>

    <Box
      mt={10}
      bg={extra.white}
      border={`1px solid ${extra.mediumBlue20pct}`}
      borderRadius="12px"
      p={5}
      boxShadow={`0px 2px 6px 0px ${extra.mediumBlue5pct}`}
      fontSize="16px"
      fontWeight={500}
    >
      Total with previously added {existingContactCount} contacts would be{' '}
      <Box
        as="span"
        display="inline-block"
        bg={secondary[500]}
        color={extra.white}
        borderRadius="full"
        px={2}
        py={1}
        fontSize="12px"
        lineHeight="14px"
        fontWeight={900}
      >
        {existingContactCount + draft.contactCount} contacts
      </Box>{' '}
      for last 7 days.
    </Box>
  </>
);

export default WeeklyReportPreview;
